import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useI18n } from '@/i18n/LanguageContext';
import { useSettings } from '@/contexts/SettingsContext';
import { supabase } from '@/lib/supabase';
import { generateHealthReport, type HealthReportData } from '@/services/healthReportGenerator';
import HealthReportView from '@/components/reports/HealthReportView';
import { Share2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface ServiceRequest {
  id: string;
  car_id: string;
  user_id: string;
  title?: string;
  owner_notes: string;
  custom_notes?: string;
  included_sections: string[];
  date_range_start?: string;
  date_range_end?: string;
  include_media: boolean;
  selected_issue_ids: string[];
  status: string;
  created_at: string;
}

interface ServiceRequestViewProps {
  requestId: string;
  onClose?: () => void;
}

const ServiceRequestView: React.FC<ServiceRequestViewProps> = ({ requestId, onClose }) => {
  const { t, formatCurrency, formatNumber } = useI18n();
  const { settings } = useSettings();
  const { toast } = useToast();
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const [reportData, setReportData] = useState<HealthReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const loadRequest = async () => {
      try {
        // Load service request
        const { data, error } = await supabase
          .from('service_requests')
          .select('*')
          .eq('id', requestId)
          .single();

        if (error) throw error;
        setRequest(data as ServiceRequest);

        // Generate report data with filters
        const fullReportData = await generateHealthReport(data.car_id);

        // Filter report data based on service request settings
        const filteredData: HealthReportData = {
          ...fullReportData,
          serviceHistory: data.date_range_start || data.date_range_end
            ? fullReportData.serviceHistory.filter(entry => {
                const entryDate = new Date(entry.serviceDate);
                const startDate = data.date_range_start ? new Date(data.date_range_start) : null;
                const endDate = data.date_range_end ? new Date(data.date_range_end) : null;
                
                if (startDate && entryDate < startDate) return false;
                if (endDate && entryDate > endDate) return false;
                return true;
              })
            : fullReportData.serviceHistory,
          activeIssues: data.selected_issue_ids.length > 0
            ? fullReportData.activeIssues.filter(issue => data.selected_issue_ids.includes(issue.id))
            : fullReportData.activeIssues,
        };

        // Remove sections not included
        if (!data.included_sections.includes('service_history')) {
          filteredData.serviceHistory = [];
        }
        if (!data.included_sections.includes('active_issues')) {
          filteredData.activeIssues = [];
        }
        if (!data.included_sections.includes('pending_tasks')) {
          filteredData.pendingTasks = [];
        }
        if (!data.included_sections.includes('fluids')) {
          filteredData.fluidHistory = [];
        }

        setReportData(filteredData);
      } catch (error) {
        console.error('Error loading service request:', error);
        toast({
          title: t('serviceRequest.error') || 'Error',
          description: error instanceof Error ? error.message : t('serviceRequest.loadError') || 'Failed to load service request',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
  }, [requestId, t, toast]);

  const handleShare = () => {
    const url = `${window.location.origin}/service-request/view/${requestId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast({
        title: t('serviceRequest.linkCopied') || 'Link copied',
        description: t('serviceRequest.linkCopiedDesc') || 'Service request link copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast({
        title: t('serviceRequest.copyError') || 'Error',
        description: t('serviceRequest.copyErrorDesc') || 'Failed to copy link',
        variant: 'destructive',
      });
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p>{t('common.loading') || 'Loading...'}</p>
      </div>
    );
  }

  if (!request || !reportData) {
    return (
      <div className="flex items-center justify-center p-8">
        <p>{t('serviceRequest.notFound') || 'Service request not found'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white" style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div className="mb-8 border-b pb-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {request.title || t('serviceRequest.title') || 'Service Request'}
            </h1>
            <p className="text-gray-600">
              {t('serviceRequest.createdAt') || 'Created'}: {format(new Date(request.created_at), 'MMM dd, yyyy')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleShare}
            className="print:hidden"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                {t('serviceRequest.copied') || 'Copied'}
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                {t('serviceRequest.share') || 'Share'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Owner Notes - Prominently displayed */}
      <section className="mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h2 className="text-xl font-semibold mb-3">
          {t('serviceRequest.ownerNotes') || 'What I want done'}
        </h2>
        <p className="text-gray-800 whitespace-pre-wrap">{request.owner_notes}</p>
      </section>

      {/* Custom Notes */}
      {request.custom_notes && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">
            {t('serviceRequest.customNotes') || 'Additional Notes'}
          </h2>
          <p className="text-gray-700 whitespace-pre-wrap">{request.custom_notes}</p>
        </section>
      )}

      {/* Report sections */}
      {reportData && <HealthReportView data={reportData} />}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t text-center text-sm text-gray-500">
        <p>{t('serviceRequest.footer') || 'Generated by On Wheels'}</p>
      </div>
    </div>
  );
};

export default ServiceRequestView;

